import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import ScriptureBlock from './tiptap-extensions/ScriptureBlock.js';
import FillInBlank from './tiptap-extensions/FillInBlank.js';
import TextAreaBlock from './tiptap-extensions/TextAreaBlock.js';
import RatingScale from './tiptap-extensions/RatingScale.js';
import NoteBlock from './tiptap-extensions/NoteBlock.js';
import StepFlow from './tiptap-extensions/StepFlow.js';
import SceneBlock from './tiptap-extensions/SceneBlock.js';
import HearEntry from './tiptap-extensions/HearEntry.js';
import TestimonyGenerator from './tiptap-extensions/TestimonyGenerator.js';
import ComprehensionCheck from './tiptap-extensions/ComprehensionCheck.js';
import ThreeCirclesWalkthrough from './tiptap-extensions/ThreeCirclesWalkthrough.js';
import CourseCards from './tiptap-extensions/CourseCards.js';
import MentorLaunch from './tiptap-extensions/MentorLaunch.js';

// Export TipTap editor for use in the platform
window.createDiscipleshipEditor = function (element, options = {}) {
  const editor = new Editor({
    element: element,
    editable: options.editable !== false, // Default to true, set false for read-only
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: options.placeholder || 'Start writing your lesson content...',
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: true,
        autolink: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      ScriptureBlock,
      FillInBlank,
      TextAreaBlock,
      RatingScale,
      NoteBlock,
      StepFlow,
      SceneBlock,
      HearEntry,
      TestimonyGenerator,
      ComprehensionCheck,
      ThreeCirclesWalkthrough,
      CourseCards,
      MentorLaunch,
    ],
    content: options.content || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
      },
    },
    onUpdate: ({ editor }) => {
      if (options.onUpdate) {
        options.onUpdate(editor.getJSON(), editor.getHTML());
      }
    },
  });

  // Add helper methods
  editor.addScriptureBlock = function (reference, version, text) {
    editor
      .chain()
      .focus()
      .setScriptureBlock({
        reference,
        version,
        text,
      })
      .run();
  };

  editor.addFillInBlank = function (question, answer, hint = '') {
    editor
      .chain()
      .focus()
      .setFillInBlank({
        question,
        answer,
        hint,
      })
      .run();
  };

  editor.addTextAreaBlock = function (
    prompt,
    placeholder = 'Type your response here...',
    isShareable = false,
    rows = 4
  ) {
    editor
      .chain()
      .focus()
      .setTextAreaBlock({
        prompt,
        placeholder,
        isShareable,
        rows,
      })
      .run();
  };

  editor.addRatingScale = function (
    prompt,
    minLabel = '1 - Distant',
    maxLabel = '10 - Very Present'
  ) {
    editor
      .chain()
      .focus()
      .setRatingScale({
        prompt,
        minLabel,
        maxLabel,
      })
      .run();
  };

  editor.addNoteBlock = function (text, type = 'note', icon = '📖', title = 'Note') {
    editor
      .chain()
      .focus()
      .setNoteBlock({
        text,
        type,
        icon,
        title,
      })
      .run();
  };

  editor.addComprehensionCheck = function (question, options, explanation = '') {
    editor
      .chain()
      .focus()
      .setComprehensionCheck({
        question,
        options: JSON.stringify(options),
        explanation,
      })
      .run();
  };

  editor.addImage = function (url) {
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return editor;
};

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { createDiscipleshipEditor: window.createDiscipleshipEditor };
}
